import { useEffect } from 'react'
import { useMap } from 'react-leaflet'
import L from 'leaflet'
import type { FullTraceData } from '../../hooks/useTrace.ts'
import { buildTraceChips } from './traceChips.ts'
import { shapeMarker } from './ShapeMarker.ts'
import {
  SUBSTATION_SYMBOL,
  TRANSFORMER_SYMBOL,
  deviceSymbol,
  markerStyle,
} from './symbology.ts'

interface TraceOverlayProps {
  data: FullTraceData
  onClear: () => void
  /** Navigate to a related entity (= MapPage setSelectedFeature). */
  onNavigate: (feature: Record<string, unknown>) => void
}

const TRACE_COLORS = {
  meter: '#22c55e',
  transformer: '#f59e0b',
  substation: '#ef4444',
  path: '#0ea5e9',       // sky-500 — the real conductor path
  feeder: '#06b6d4',     // cyan-500 — the fk_only fallback route line
  device: '#a855f7',     // purple-500
  deviceOpen: '#ef4444',
  secondary: '#8b5cf6',
}

/** Dedicated high-z pane for the trace, above the (dimmed) overlay/marker panes,
 *  below tooltips. Mirrors SELECTION_PANE. pointer-events stays enabled so device
 *  markers remain clickable. */
export const TRACE_PANE = 'trace-pane'
function ensureTracePane(map: L.Map): void {
  if (!map.getPane(TRACE_PANE)) {
    const pane = map.createPane(TRACE_PANE)
    pane.style.zIndex = '645'
  }
}

/**
 * Trace visualization overlay (Phase 1C v2): the real conductor path polyline
 * from path_segments, purple diamonds for each path device, and a clickable
 * breadcrumb of chips. All layers go into TRACE_PANE so the CSS `.trace-active`
 * dim on the standard panes never touches them.
 */
export function TraceOverlay({ data, onClear, onNavigate }: TraceOverlayProps) {
  const map = useMap()
  const trace = data.trace
  const secondary = trace.secondary
  const chips = buildTraceChips(trace)

  useEffect(() => {
    ensureTracePane(map)
    // preferCanvas on the MapContainer (D1) makes Leaflet's pane-renderer
    // cache (_getPaneRenderer) default custom panes to Canvas too. Canvas
    // sets no pointer-events on its own — the trace pane (~120% viewport,
    // pointer-events enabled by ensureTracePane) would become a full click
    // shield over the map, and Canvas ignores `className` so .trace-pulse
    // would stop animating. Force this pane's vectors back onto an explicit
    // SVG renderer to keep both working; created fresh per trace and removed
    // in cleanup alongside layerGroup so it never leaks an empty <svg>.
    const renderer = L.svg({ pane: TRACE_PANE })
    const layerGroup = L.layerGroup().addTo(map)
    const bounds: L.LatLng[] = []

    // 1. Real conductor path (ordered per-segment features, already oriented).
    const fc = trace.path_segments
    if (fc && fc.features.length > 0) {
      for (const feat of fc.features) {
        const coords = feat.geometry?.coordinates ?? []
        const latLngs = coords.map(([lng, lat]) => {
          const ll = L.latLng(lat, lng)
          bounds.push(ll)
          return ll
        })
        if (latLngs.length >= 2) {
          L.polyline(latLngs, {
            pane: TRACE_PANE, renderer, color: TRACE_COLORS.path, weight: 5, opacity: 0.95,
          }).addTo(layerGroup)
        }
      }
    }

    // 1b. fk_only fallback: no computed conductor path — draw the embedded feeder
    // route as the connecting line so the trace still shows a path (preserves the
    // pre-1C visual in the no-protection-run / no-geometry case). Bounds stay
    // marker-based, matching today's fitBounds behavior.
    if ((!fc || fc.features.length === 0) && trace.feeder_route) {
      const routeLatLngs = trace.feeder_route.coordinates.map(([lng, lat]) => L.latLng(lat, lng))
      if (routeLatLngs.length >= 2) {
        L.polyline(routeLatLngs, {
          pane: TRACE_PANE, renderer, color: TRACE_COLORS.feeder, weight: 4, opacity: 0.7,
        })
          .bindTooltip(`Feeder: ${trace.feeder?.name || trace.feeder?.code || trace.feeder?.id}`)
          .addTo(layerGroup)
      }
    }

    // 2. Secondary attach-segment geometry (MultiLineString), when present.
    const secPath = trace.secondary_path as { coordinates?: number[][][] } | null
    if (secPath?.coordinates) {
      for (const line of secPath.coordinates) {
        const latLngs = line.map(([lng, lat]) => L.latLng(lat, lng))
        if (latLngs.length >= 2) {
          L.polyline(latLngs, {
            pane: TRACE_PANE, renderer, color: TRACE_COLORS.secondary, weight: 4, opacity: 0.9,
          }).addTo(layerGroup)
        }
      }
    }

    // 3. Meter marker (pulsing green).
    const meterLoc = trace.meter_location
    if (meterLoc) {
      const [lng, lat] = meterLoc.coordinates
      const ll = L.latLng(lat, lng)
      bounds.push(ll)
      L.circleMarker(ll, {
        pane: TRACE_PANE, renderer, radius: 14, fillColor: TRACE_COLORS.meter,
        color: TRACE_COLORS.meter, weight: 2, fillOpacity: 0.2,
        className: 'trace-pulse',
      }).addTo(layerGroup)
      L.circleMarker(ll, {
        pane: TRACE_PANE, renderer, radius: 6, fillColor: TRACE_COLORS.meter, color: '#fff',
        weight: 2, fillOpacity: 1,
      })
        .bindTooltip(`Meter: ${trace.meter.meter_number || trace.meter.id}`)
        .addTo(layerGroup)
    }

    // 4. Devices along the path — square for switching, diamond for
    //    interrupting, down-triangle for capacitors, hollow when open. The
    //    trace payload carries `is_open` (derived in the trace service); map
    //    layers read `normal_status`.
    for (const d of trace.devices) {
      if (!d.location) continue
      const [lng, lat] = d.location.coordinates
      const ll = L.latLng(lat, lng)
      bounds.push(ll)
      const color = d.is_open ? TRACE_COLORS.deviceOpen : TRACE_COLORS.device
      const symbol = deviceSymbol(d.device_type, d.normal_status)
      shapeMarker(ll, {
        pane: TRACE_PANE,
        renderer,
        shape: symbol.shape,
        radius: symbol.size,
        ...markerStyle(color, symbol.hollow),
      })
        .bindTooltip(
          `${d.name}${d.is_open ? ' (normally OPEN)' : ''}` +
          `${d.distance_ft != null ? ` · ${Math.round(d.distance_ft)} ft` : ''}`,
        )
        .addTo(layerGroup)
    }

    // 5. Transformer + substation markers.
    const xfmrLoc = trace.transformer_location
    if (xfmrLoc) {
      const [lng, lat] = xfmrLoc.coordinates
      const ll = L.latLng(lat, lng)
      bounds.push(ll)
      shapeMarker(ll, {
        pane: TRACE_PANE, renderer,
        shape: TRANSFORMER_SYMBOL.shape, radius: 10,
        fillColor: TRACE_COLORS.transformer,
        color: '#fff', weight: 2, fillOpacity: 0.9,
      })
        .bindTooltip(`Transformer: ${trace.transformer?.name || trace.transformer?.id}`)
        .addTo(layerGroup)
    }
    const subLoc = trace.substation_location
    if (subLoc) {
      const [lng, lat] = subLoc.coordinates
      const ll = L.latLng(lat, lng)
      bounds.push(ll)
      shapeMarker(ll, {
        pane: TRACE_PANE, renderer,
        shape: SUBSTATION_SYMBOL.shape, radius: 12,
        fillColor: TRACE_COLORS.substation,
        color: '#fff', weight: 3, fillOpacity: 0.9,
      })
        .bindTooltip(`Substation: ${trace.substation?.name || trace.substation?.id}`)
        .addTo(layerGroup)
    }

    // 6. Fit bounds.
    if (bounds.length >= 2) {
      map.fitBounds(L.latLngBounds(bounds).pad(0.3), { maxZoom: 17, duration: 0.8 })
    } else if (bounds.length === 1) {
      map.flyTo(bounds[0], 17, { duration: 0.8 })
    }

    return () => {
      layerGroup.remove()
      renderer.remove()
    }
  }, [trace, map])

  const basisLabel: Record<string, string> = {
    persisted: 'Traced path',
    live_fallback: 'Traced path (live)',
    fk_only: 'FK path only',
  }
  const status = trace.trace_basis === 'fk_only'
    ? { label: basisLabel.fk_only, color: 'text-orange-700 bg-orange-50 border-orange-200' }
    : { label: basisLabel[trace.trace_basis], color: 'text-green-700 bg-green-50 border-green-200' }
  // Persisted basis provenance: the protection run's finish date. Only the persisted
  // path has a run (live_fallback / fk_only carry run_finished_at = null).
  const runDate = trace.run_finished_at
    ? new Date(trace.run_finished_at).toLocaleDateString()
    : null

  return (
    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[1000] bg-white rounded-lg shadow-lg px-4 py-3 max-w-2xl">
      <div className="flex items-center gap-2 text-sm flex-wrap">
        <span
          className={`px-2 py-0.5 rounded-full text-xs font-medium border ${status.color}`}
          title={runDate ? `Protection run finished ${runDate}` : undefined}
        >
          {status.label}{runDate ? ` · run ${runDate}` : ''}
        </span>

        {/* Clickable breadcrumb chips */}
        <div className="flex items-center gap-1 text-xs flex-wrap">
          {chips.map((chip, i) => (
            <span key={chip.key} className="flex items-center gap-1">
              {i > 0 && <span className="text-gray-300">&rarr;</span>}
              {chip.navTo ? (
                <button
                  onClick={() => onNavigate(chip.navTo!)}
                  className="font-medium hover:underline"
                  style={{ color: chip.color }}
                  title={`Go to ${chip.label}`}
                >
                  {chip.label}
                </button>
              ) : (
                <span className="font-medium" style={{ color: chip.color }}
                  title={chip.isOpen ? 'Normally-open device — verify field state' : undefined}>
                  {chip.label}{chip.isOpen ? ' ⚠' : ''}
                </span>
              )}
            </span>
          ))}
        </div>

        {/* Provenance */}
        {trace.graph_basis && (
          <span className="text-[11px] text-gray-400" title={`graph basis: ${trace.graph_basis}`}>
            · {trace.graph_basis}
          </span>
        )}

        <button
          onClick={onClear}
          className="ml-auto px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
        >
          Clear
        </button>
      </div>

      {/* Open-point annotations (spec §4.3 — annotate, never block) */}
      {trace.open_point_warnings.length > 0 && (
        <div className="mt-2 text-xs text-red-600 space-y-0.5">
          {trace.open_point_warnings.map((w, i) => (
            <div key={i}>⚠ {w}</div>
          ))}
        </div>
      )}

      {secondary?.available && (
        <div className="mt-1 text-[11px] text-violet-600">
          Secondary leg: {secondary.path_method ?? 'derived'}
        </div>
      )}
    </div>
  )
}
